import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { LogOut, Home, Plus, Trash2, FileText, Send, Check, Clock } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminContracts() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showSend, setShowSend] = useState(null);
  const [form, setForm] = useState({ name: '', content: '', service_type: 'coaching' });
  const [sendForm, setSendForm] = useState({ client_name: '', client_email: '', client_id: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [tplRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/contracts/templates`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/contracts`, { withCredentials: true }),
      ]);
      setTemplates(tplRes.data);
      setContracts(cRes.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const createTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/contracts/templates`, form, { withCredentials: true });
      toast.success('Template created');
      setShowCreate(false);
      setForm({ name: '', content: '', service_type: 'coaching' });
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/contracts/templates/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const sendContract = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/admin/contracts/send`, {
        template_id: showSend,
        ...sendForm
      }, { withCredentials: true });
      toast.success('Contract sent');
      setShowSend(null);
      setSendForm({ client_name: '', client_email: '', client_id: '' });
      fetchAll();
    } catch { toast.error('Failed to send'); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };
  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <AdminLayout title="Contracts"><div>
        <div className="flex gap-3 mb-6">
          <button onClick={() => setActiveTab('templates')} className={`px-4 py-2 text-sm font-medium rounded-lg border ${activeTab === 'templates' ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`} data-testid="tab-templates">Templates</button>
          <button onClick={() => setActiveTab('sent')} className={`px-4 py-2 text-sm font-medium rounded-lg border ${activeTab === 'sent' ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`} data-testid="tab-sent">Sent Contracts ({contracts.length})</button>
        </div>

        {activeTab === 'templates' && (
          <div>
            <div className="flex justify-between mb-4">
              <h2 className="text-xl font-bold text-[#0A0F1C]">Contract Templates</h2>
              <Button onClick={() => setShowCreate(true)} className="btn-primary-blue flex items-center gap-2" data-testid="create-contract-tpl"><Plus size={16} /> New Template</Button>
            </div>
            <div className="space-y-3">
              {templates.map((t, i) => (
                <div key={t.template_id} className="bg-white p-5 border border-[#E2E8F0] rounded-lg" data-testid={`contract-tpl-${i}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2"><span className="font-bold text-[#0A0F1C]">{t.name}</span><span className="text-xs px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">{t.service_type}</span></div>
                      <p className="text-sm text-[#475569] mt-1">{t.content?.substring(0, 100)}...</p>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => { setShowSend(t.template_id); }} className="btn-outline text-xs flex items-center gap-1" data-testid={`send-contract-${i}`}><Send size={14} /> Send</Button>
                      <button onClick={() => deleteTemplate(t.template_id)} className="text-red-600 hover:bg-red-50 p-2 rounded"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'sent' && (
          <div>
            <h2 className="text-xl font-bold text-[#0A0F1C] mb-4">Sent Contracts</h2>
            <div className="space-y-3">
              {contracts.map((c, i) => (
                <div key={c.contract_id} className="bg-white p-5 border border-[#E2E8F0] rounded-lg" data-testid={`sent-contract-${i}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold text-[#0A0F1C]">{c.client_name}</span>
                      <span className="text-sm text-[#475569] ml-2">{c.client_email}</span>
                      <div className="text-xs text-[#94A3B8] mt-1">Sent: {new Date(c.sent_at).toLocaleDateString()} | {c.service_type}</div>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1 ${c.status === 'signed' ? 'bg-green-100 text-green-700' : 'bg-[#FFF7ED] text-[#F97316]'}`}>
                      {c.status === 'signed' ? <Check size={12} /> : <Clock size={12} />} {c.status}
                    </span>
                  </div>
                </div>
              ))}
              {contracts.length === 0 && <p className="text-[#475569] text-center py-8">No contracts sent yet</p>}
            </div>
          </div>
        )}

        {/* Create Template Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold mb-4">Create Contract Template</h3>
              <form onSubmit={createTemplate} className="space-y-3">
                <Input placeholder="Template Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="contract-name-input" />
                <select value={form.service_type} onChange={(e) => setForm({...form, service_type: e.target.value})} className="form-input w-full">
                  <option value="coaching">Coaching</option>
                  <option value="program">Program</option>
                  <option value="consulting">Consulting</option>
                </select>
                <Textarea placeholder="Contract content (use {{client_name}} and {{date}} as variables)" value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} className="min-h-[200px]" required />
                <div className="flex gap-3"><Button type="button" onClick={() => setShowCreate(false)} className="btn-outline flex-1">Cancel</Button><Button type="submit" className="btn-primary-blue flex-1">Create</Button></div>
              </form>
            </div>
          </div>
        )}

        {/* Send Contract Modal */}
        {showSend && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-bold mb-4">Send Contract</h3>
              <form onSubmit={sendContract} className="space-y-3">
                <Input placeholder="Client Name" value={sendForm.client_name} onChange={(e) => setSendForm({...sendForm, client_name: e.target.value})} required data-testid="send-client-name" />
                <Input type="email" placeholder="Client Email" value={sendForm.client_email} onChange={(e) => setSendForm({...sendForm, client_email: e.target.value})} required />
                <div className="flex gap-3"><Button type="button" onClick={() => setShowSend(null)} className="btn-outline flex-1">Cancel</Button><Button type="submit" className="btn-primary-blue flex-1">Send</Button></div>
              </form>
            </div>
          </div>
        )}
      </div></AdminLayout>);}
